// Concern score — a 0-100 heuristic that surfaces calls likely worth
// human review. Not a measure of caller sentiment (we can't read
// transcripts); it's a measure of "did this call look rough from the
// metadata we DO have access to?".
//
// Weights are deliberately simple + documented so operators can trust the
// score and tune it. If a signal is missing we skip it rather than
// pretending it's zero — a short session with no eval data shouldn't
// score high just because it's short.
//
// Score composition (all additive, each capped so no single factor dominates):
//
//   OUTCOME                     up to 40 pts
//     ABANDONED / FAILED → 40
//     ESCALATED           → 25
//     HANDLED / COMPLETED → 0
//     unknown             → 10
//
//   EVAL SCORE                   up to 25 pts
//     overallEvalScore < 0.5     → 25
//     < 0.7                      → 15
//     < 0.85                     → 5
//     missing or >= 0.85         → 0
//
//   BOT HEALTH                   up to 20 pts
//     evalInteractionsFailureCount >= 3 → 10
//     deadAirCount > 2                   → 5
//     bargeInCount > 3                   → 5
//
//   RESPONSE SPEED               up to 10 pts
//     averageResponseMs > 5000  → 10
//     averageResponseMs > 3000  → 5
//
//   MESSAGE VOLUME ANOMALY       up to 5 pts
//     noOfTotalMessages >= 25   → 5   (unusually long bot session, often "stuck")
//     noOfTotalMessages <= 2    → 5   (hangup during welcome)

export function scoreCall(row) {
  if (!row) return { score: 0, factors: [] };
  const factors = [];
  let score = 0;

  const outcomePts = scoreOutcome(row.outcome);
  if (outcomePts.points > 0) {
    score += outcomePts.points;
    factors.push({ label: outcomePts.label, points: outcomePts.points });
  }

  const evalPts = scoreEval(row);
  if (evalPts.points > 0) {
    score += evalPts.points;
    factors.push({ label: evalPts.label, points: evalPts.points });
  }

  const botPts = scoreBotHealth(row);
  for (const f of botPts) {
    score += f.points;
    factors.push(f);
  }

  const latencyPts = scoreLatency(row);
  if (latencyPts.points > 0) {
    score += latencyPts.points;
    factors.push({ label: latencyPts.label, points: latencyPts.points });
  }

  const volumePts = scoreMessageVolume(row);
  if (volumePts.points > 0) {
    score += volumePts.points;
    factors.push({ label: volumePts.label, points: volumePts.points });
  }

  return { score: Math.min(score, 100), factors };
}

function scoreOutcome(outcome) {
  switch (outcome) {
    case 'ABANDONED':
      return { points: 40, label: 'Caller abandoned' };
    case 'FAILED':
      return { points: 40, label: 'Call failed' };
    case 'ESCALATED':
      return { points: 25, label: 'Escalated to human' };
    case 'HANDLED':
    case 'COMPLETED':
      return { points: 0, label: '' };
    default:
      return outcome == null
        ? { points: 10, label: 'Outcome unknown' }
        : { points: 0, label: '' };
  }
}

function scoreEval(row) {
  const s = row.overallEvalScore;
  if (s == null) return { points: 0, label: '' };
  if (s < 0.5) return { points: 25, label: `Eval score ${s.toFixed(2)}` };
  if (s < 0.7) return { points: 15, label: `Eval score ${s.toFixed(2)}` };
  if (s < 0.85) return { points: 5, label: `Eval score ${s.toFixed(2)}` };
  return { points: 0, label: '' };
}

function scoreBotHealth(row) {
  const factors = [];
  if (row.evalInteractionsFailureCount != null && row.evalInteractionsFailureCount >= 3) {
    factors.push({ points: 10, label: `${row.evalInteractionsFailureCount} eval failures` });
  }
  if (row.deadAirCount != null && row.deadAirCount > 2) {
    factors.push({ points: 5, label: `${row.deadAirCount}× dead air` });
  }
  if (row.bargeInCount != null && row.bargeInCount > 3) {
    factors.push({ points: 5, label: `${row.bargeInCount}× barge-in` });
  }
  return factors;
}

function scoreLatency(row) {
  const ms = row.avgResponseMs;
  if (ms == null) return { points: 0, label: '' };
  if (ms > 5000) return { points: 10, label: `${(ms / 1000).toFixed(1)}s avg response` };
  if (ms > 3000) return { points: 5, label: `${(ms / 1000).toFixed(1)}s avg response` };
  return { points: 0, label: '' };
}

function scoreMessageVolume(row) {
  const m = row.messageCount;
  if (m == null) return { points: 0, label: '' };
  if (m >= 25) return { points: 5, label: `${m} msgs (long session)` };
  if (m <= 2) return { points: 5, label: `${m} msgs (very short)` };
  return { points: 0, label: '' };
}

export function concernColor(score) {
  if (score >= 60) return 'red';
  if (score >= 35) return 'orange';
  if (score >= 15) return 'yellow';
  return 'gray';
}

export function concernLabel(score) {
  if (score >= 60) return 'high';
  if (score >= 35) return 'medium';
  if (score >= 15) return 'low';
  return 'none';
}
